import { useMemo } from "react";
import type { CreferenceEntityType } from "./CreferenceEntity";
import {
  resolveCreferenceLabelState,
  selectCreferenceIsLoading,
  selectCreferenceLabelParts,
  type ReferenceCollab,
  type ReferenceFile,
  type ReferenceMember,
  type ReferenceProduct,
  type ReferenceVersion,
} from "./creferenceResolvedEntitySelectors";

interface UseCreferenceLabelStateInput {
  collab: ReferenceCollab | null;
  currentUserName: string | null;
  effectiveEntityType: CreferenceEntityType;
  file: ReferenceFile | null;
  isCurrentUserReference: boolean;
  member: ReferenceMember | null;
  product: ReferenceProduct | null;
  profileLoading: boolean;
  sessionLoading: boolean;
  version: ReferenceVersion | null;
  versionProduct: ReferenceProduct | null;
}

export interface CreferenceLabelState {
  isLoading: boolean;
  isUnresolved: boolean;
  label: string;
  /** Rendered after the label's truncation, never inside its budget. */
  labelSuffix: string | null;
}

export function useCreferenceLabelState({
  collab,
  currentUserName,
  effectiveEntityType,
  file,
  isCurrentUserReference,
  member,
  product,
  profileLoading,
  sessionLoading,
  version,
  versionProduct,
}: UseCreferenceLabelStateInput): CreferenceLabelState {
  const isLoading = useMemo(
    () => selectCreferenceIsLoading({
      entityType: effectiveEntityType,
      isCurrentUserReference,
      profileLoading,
      sessionLoading,
    }),
    [
      effectiveEntityType,
      isCurrentUserReference,
      profileLoading,
      sessionLoading,
    ],
  );
  const resolvedParts = useMemo(
    () => selectCreferenceLabelParts({
      collab,
      currentUserName,
      entityType: effectiveEntityType,
      file,
      member,
      product,
      version,
      versionProduct,
    }),
    [
      collab,
      currentUserName,
      effectiveEntityType,
      file,
      member,
      product,
      version,
      versionProduct,
    ],
  );

  return {
    isLoading,
    ...resolveCreferenceLabelState({ isLoading, resolvedParts }),
  };
}
